import { useState, useEffect, useCallback } from 'react';

import { Geochart } from "../Charts/Geochart";
import { Table } from "../Charts/Table.js";
import { Chart } from "../Charts/Chart.js";
import { downloadCSV } from "../utility/downloadHelper.js";

import {
    createDataTable,
    createDataChart,
    createDataMap,
    getData,
    getYears
} from "../utility/CreateData.js";

import {
    MapProperties,
    TableProperties,
    ChartProperties
} from "../utility/VizProperties.js";

export const Viz = () => {
    //General Input Indicators
    const [indicator, setIndicator] = useState("Geothermal Gross Electrical generation GWh/yr");
    const [indicatorTotal, setIndicatorTotal] = useState(["Geothermal Gross Electrical generation GWh/yr", 'electricityData', 3]);
    const [country, setCountry] = useState([{ value: "World", label: "World" }]);
    const [year, setYear] = useState('2023');
    const [chartType, setChartType] = useState('Bar');
    const [tableType, setTableType] = useState('List');

    //Other Data
    const [viz, setViz] = useState("Chart");
    const [currentData, setCurrentData] = useState([]); // Holds the currently displayed data
    const [newData, setNewData] = useState([]); // Holds the data being fetched
    const [geodata, setGeoData] = useState([]);
    const [yearsList, setYearsList] = useState([]);
    const [exportFunctionCSV, setExportFunctionCSV] = useState(null);  // State to hold export function from Table
    const [exportFunctionPDF, setExportFunctionPDF] = useState(null);  // State to hold export function from Table

    const [loading, setLoading] = useState(false);

    const mapClass = viz === "Map" ? 'vizButtonActive miniTitleText mediumFont' : 'vizButton lightFont paragraphText hoverableVizH1';
    const tableClass = viz === "Table" ? 'vizButtonActive miniTitleText mediumFont' : 'vizButton lightFont paragraphText hoverableVizH1';
    const chartClass = viz === "Chart" ? 'vizButtonActive miniTitleText mediumFont' : 'vizButton lightFont paragraphText hoverableVizH1';

    const vizClass = viz === "Map" ? 'grid vizMap' : viz === "Table" ? 'grid vizTable' : 'grid vizChart';
    let vizWrapperClass = viz === "Map" ? "visualisations visualisationMap" : viz === "Table" ? "visualisations visualisationTable" : "visualisations";

    const fetchCountriesData = async () => {
        setLoading(true);

        const geodata = await getData();
        const years = await getYears(geodata);

        setGeoData([...geodata]);

        if (years.length > 0) {
            setYearsList([...years]);
            setYear(prevYear => (years.includes(prevYear) ? prevYear : years[0])); // Default to the first year if current year isn't valid
        }
        setLoading(false);
    }

    useEffect(() => {
        !loading && fetchCountriesData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            let fetchedData = [];

            if (viz === 'Table') {
                fetchedData = await createDataTable(indicatorTotal, country, year);
            } else if (viz === 'Chart') {
                if (chartType === 'Pile' && country.length > 0) {
                    // Fetch data for each year in yearsList
                    fetchedData = await Promise.all(
                        yearsList.map(async (yearItem) => {
                            return await createDataChart(indicatorTotal, country, yearItem);
                        })
                    );
                } else {
                    fetchedData = await createDataChart(indicatorTotal, country, year);
                }
            } else if (viz === 'Map') {
                fetchedData = await createDataMap(indicatorTotal, country, year);
            }

            setNewData(fetchedData);
            setLoading(false);
        };

        fetchData();
    }, [viz, indicatorTotal, country, year, chartType, tableType]);

    // Effect to update currentData only after newData is fetched
    useEffect(() => {
        if (!loading && newData.length > 0) {
            setCurrentData(newData); // Swap in the newData once fetched
        } else {
            setCurrentData(null);
        }
    }, [newData, loading]);

    // Helper function to set indicators based on input type
    const setIndicatorByValue = (value, type) => {
        const indicatorMap = {
            //Map
            'Geothermal Gross Electrical generation GWh/yr': ["Geothermal Gross Electrical generation GWh/yr", 'electricityData', 3],
            'Geothermal Installed Capacity (MWe)': ["Geothermal Installed Capacity (MWe)", 'electricityData', 3],
            'Total Energy used (TJ/year)': ["Total Energy used (TJ/year)", 'summaryData', 4],
            'Total Installed Capacity (MWt)': ["Total Installed Capacity (MWt)", 'summaryData', 4],
            'combinedGeothermalHcInstalledCapacity': ["Combined Geothermal and H&C installed capacity", 'combinedCapacity', 0],
            'combinedGeothermalHcPowerProduction': ["Combined Geothermal and H&C power production", 'combinedProduction', 0],
            //Chart
            'Bar': ["Geothermal Gross Electrical generation GWh/yr", 'electricityData', 3],
            'Pie': ["Installed Capacity (MW)", 'powerData', 1],
            'Pile': ["Installed Capacity (MW)", 'powerData', 1],
            'summaryData': ["", 'summaryData', 4],
            'electricityData': ["", 'electricityData', 3],
            'Total Gross Electrical generation GWh/yr': ["Geothermal Gross Electrical generation GWh/yr", 'electricityData', 3],
            'Total Installed Capacity (MWe)': ["Geothermal Installed Capacity (MWe)", 'electricityData', 3],
            'Gross Energy Produced (GEP) (GWh/year)': ["Gross Energy Produced (GEP) (GWh/year)", 'powerData', 1],
            'Installed Capacity (MW)': ["Installed Capacity (MW)", 'powerData', 1],
            'Nominal Capacity (MWt)': ["Nominal Capacity (MWt)", 'hcData', 0],
            'Energy used (net) (TJ/year)': ["Energy used (net) (TJ/year)", 'hcData', 0],
            //Table
            'List': ["", 'powerData', 1],
            'Report': ["", 'powerDataReport', 1],
            'powerData': ["", 'powerData', 1],
            'hcData': ["", 'hcData', 0],
            'countries': ["", 'countries', 0],
            'power': ["", 'power', 1],
            'hc': ["", 'hc', 0],
            'powerDataReport': ["", 'powerDataReport', 1],
            'powerOperation': ["", 'powerOperation', 1],
            'hcProjects': ["", 'hcProjects', 0],
            'powerTotal': ["", 'powerTotal', 1],
            'hcTotal': ["", 'hcTotal', 0],
        };

        if (indicatorMap[value]) {
            setIndicatorTotal(indicatorMap[value]);
            setIndicator(value);
        }
    };

    // INPUT SELECTION CONTROLLERS

    const handleInputChart = (value, type) => {
        switch (type) {
            case 'year':
                setYear(value);
                break;
            case 'country':
                setCountry(value);
                break;
            case 'chartType':
                setChartType(value);
                setIndicatorByValue(value, 'chartType');
                break;
            case 'indicator':
                setIndicatorByValue(value, 'indicator');
                break;
            default:
                break;
        }
    };

    const handleInputMap = (value, type) => {
        switch (type) {
            case 'country':
                setCountry(value);
                break;
            case 'indicator':
                setIndicatorByValue(value, 'indicator');
                break;
            default:
                break;
        }
    };

    const handleInputTable = (value, type) => {
        switch (type) {
            case 'year':
                setYear(value);
                break;
            case 'country':
                setCountry(value);
                break;
            case 'tableType':
                setTableType(value);
                setIndicatorByValue(value, 'tableType');
                break;
            case 'indicator':
                setIndicatorByValue(value, 'indicator');
                break;
            default:
                break;
        }
    };

    const changeViz = async (event) => {
        const vizMap = {
            'Browse as Map': {
                viz: 'Map',
                indicatorValue: "Geothermal Gross Electrical generation GWh/yr",
                reset: () => {
                    setTableType("List");
                    setChartType("Bar");
                }
            },
            'Browse as Table': {
                viz: 'Table',
                indicatorValue: "powerData",
                reset: () => {
                    setChartType("Bar");
                    setTableType("List"); // reset to default
                }
            },
            'Browse as Chart': {
                viz: 'Chart',
                indicatorValue: "Total Gross Electrical generation GWh/yr",
                reset: () => {
                    setTableType("List");
                    setChartType("Bar"); // reset to default
                }
            },
        };

        const selectedViz = vizMap[event.currentTarget.textContent];
        if (selectedViz) {
            selectedViz.reset(); // Reset state specific to the viz
            setViz(selectedViz.viz);
            setIndicatorByValue(selectedViz.indicatorValue, 'indicator');
        }
    };

    // Snap to the nearest valid year in the array
    const sliderChange = (event) => {
        const value = event.target.value;
        const closestYear = yearsList.reduce((prev, curr) => {
            return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
        });
        setYear(closestYear);
    };

    const triggerExportPDF = useCallback((exportFunc) => {
        setExportFunctionPDF(() => exportFunc);
    }, []);

    const triggerExportCSV = useCallback((exportFunc) => {
        setExportFunctionCSV(() => exportFunc);  // Directly set the export function
    }, []);

    const handleExportClickCSV = () => {
        if (exportFunctionCSV) {
            exportFunctionCSV();  // Call the export function when the export button is clicked
        } else {
            console.log('Export function not available yet');
        }
    };

    const handleExportClickPDF = () => {
        if (exportFunctionPDF) {
            exportFunctionPDF();  // Call the export function when the export button is clicked
        } else {
            console.log('Export function not available yet');
        }
    };

    return (
        <section className={vizWrapperClass}>
            <div className='grid'>
                <div id='chartButtons'>
                    <p className={chartClass} onClick={changeViz} id='chartButton'>Browse as Chart</p>
                    <p className={mapClass} onClick={changeViz} id='mapButton'>Browse as Map</p>
                    <p className={tableClass} onClick={changeViz} id='tableButton'>Browse as Table</p>
                </div>
                <div id='properties' className="grid">
                    {viz === "Map" && <MapProperties
                        handleInput={handleInputMap}
                        indicator={indicator}
                        country={country}
                        yearsList={yearsList}
                        handleExportClickPDF={handleExportClickPDF}
                    />}
                    {viz === "Table" && <TableProperties
                        handleInput={handleInputTable}
                        indicator={indicator}
                        country={country}
                        tableType={tableType}
                        year={year}
                        geodata={geodata}
                        yearsList={yearsList}
                        handleExportClickPDF={handleExportClickPDF}
                        handleExportClickCSV={handleExportClickCSV}
                    />}
                    {viz === "Chart" && <ChartProperties
                        handleInput={handleInputChart}
                        indicator={indicator}
                        country={country}
                        chartType={chartType}
                        year={year}
                        geodata={geodata}
                        yearsList={yearsList}
                        handleExportClickPDF={handleExportClickPDF}
                    />}
                </div>
                <div id='viz' className={vizClass}>
                    {!loading && yearsList.length > 0 ? (
                        viz === 'Map' ? (
                            <Geochart
                                data={currentData}
                                property={indicator}
                                year={year}
                                triggerExportPDF={triggerExportPDF}
                                country={country}
                            />
                        ) : viz === 'Table' ? (
                            <Table
                                data={currentData}
                                property={indicator}
                                year={year}
                                triggerExportPDF={triggerExportPDF}
                                triggerExportCSV={triggerExportCSV}
                                country={country}
                            />
                        ) : viz === 'Chart' ? (
                            <Chart
                                data={currentData}
                                chartType={chartType}
                                year={year}
                                property={indicator}
                                triggerExportPDF={triggerExportPDF}
                                country={country}
                            />
                        ) : (
                            <div>Invalid visualisation type</div>
                        )
                    ) : (
                        <div id='loadingData'>Loading...</div>
                    )}
                </div>

                {viz === "Map" && (
                    <div id="timeline" className="grid">
                        <div id="year" className="timelineButtons">{year}</div>
                        {yearsList.length > 0 && (
                            <div className="slider-container">
                                <input
                                    type="range"
                                    id="slider"
                                    value={year}
                                    min={Math.min(...yearsList)}
                                    max={Math.max(...yearsList)}
                                    step="1"
                                    onChange={sliderChange}
                                />
                                <div className="ticks">
                                    {yearsList.map((yr) => {
                                        const minYear = Math.min(...yearsList);
                                        const maxYear = Math.max(...yearsList);
                                        // Calculate the left offset in percentage:
                                        const leftPos = ((yr - minYear) / (maxYear - minYear)) * 100;
                                        return (
                                            <span
                                                key={yr}
                                                className="tick"
                                                title={yr}
                                                style={{
                                                    left: `${leftPos}%`,
                                                    transform: 'translateX(-50%)',
                                                    position: 'absolute'
                                                }}
                                            >
                                                {yr}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div id='credits' style={viz === "Table" ? { transform: 'translateY(120%)' } : {}}>Source: <a href="https://worldgeothermal.org/geothermal-data/conference-paper-database">World Geothermal Conference proceedings</a>, Graphics: <a href="https://creativecommons.org/licenses/by/4.0/">Manzella A. and Trumpy E. (CNR-IGG) Licence CC BY 4.0</a></div>
            </div>
        </section>
    );
};